import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';

import { erc20Abi, parseUnits } from 'viem';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';

import useAppStore from '@/stores/appStore';
import useRaceStore from '@/stores/raceStore';
import { useEnvAddresses } from '@/hooks/useEnvAddresses';

import wizheader from '@/assets/wizheader.jpg';
import StepCards from '@/components/wizard/step-cards';
import StepViews from '@/components/wizard/step-views';
import StepIndicator from '@/components/wizard/step-indicator';
import RaceReviewModal from '@/components/modals/race-review-modal';

import { getEnvChain } from '@/config/chainConfig';
import { tokenRacerAbi } from '@/abi/tokenRacerAbi';
import { useConnectModal } from '@rainbow-me/rainbowkit';

const TransactionStatus = {
  WRONG_NETWORK: 'WRONG_NETWORK',
  INSUFFICIENT_ALLOWANCE: 'INSUFFICIENT_ALLOWANCE',
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
  READY_TO_SUBMIT: 'READY_TO_SUBMIT',
};

const TransactionStatusText = {
  [TransactionStatus.WRONG_NETWORK]: 'Switch Network',
  [TransactionStatus.INSUFFICIENT_ALLOWANCE]: 'Approve',
  [TransactionStatus.INSUFFICIENT_BALANCE]: 'Insufficient balance',
  [TransactionStatus.READY_TO_SUBMIT]: 'Submit',
};

export type Token = {
  name: string;
  id: string;
  symbol: string;
  image: string;
};

export type HandicapSettings = {
  enabled: boolean;
};

export type RaceData = {
  raceName?: string;
  raceDuration?: number;
  startDate?: string;
  startTime?: string;
  startDateTime?: dayjs.Dayjs;
  handicapSettings?: {
    enabled: boolean;
  };
  usdcAmount?: number;
  allocations?: {
    [key: string]: number;
  };
  creatorCommission?: number;
  selectedTokens?: Token[];
};

const Index = () => {
  const publicClient = usePublicClient({ chainId: getEnvChain().id });
  const { data: walletClient } = useWalletClient();
  const { chainId, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { tokenRacer, usdc } = useEnvAddresses();
  const { fetchActiveRaces } = useRaceStore();
  const navigate = useNavigate();
  const { isRestricted } = useAppStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTokens, setSelectedTokens] = useState<Token[]>([]);
  const [transactionStatus, setTransactionStatus] = useState(TransactionStatus.INSUFFICIENT_ALLOWANCE);
  const [waitingForTransaction, setWaitingForTransaction] = useState(false);

  const totalSteps = 3;

  // New state to accumulate data from each step
  const [raceData, setRaceData] = useState<RaceData>({});
  // State to control review modal visibility
  const [showReview, setShowReview] = useState(false);

  const handleNextStep = (data: Partial<RaceData>) => {
    // Merge current step's data into the raceData object if any data is passed
    if (data) {
      setRaceData(prev => ({ ...prev, ...data }));
      // Special handling for step 1 to capture selected tokens
      if (currentStep === 1 && data.selectedTokens) {
        setSelectedTokens(data.selectedTokens);
      }
    }
    // If it's the last step, instead of advancing, show the review modal
    if (currentStep === totalSteps) {
      setShowReview(true);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const checkTransactionStatus = useCallback(async () => {
    if (!walletClient || isNaN(Number(raceData.usdcAmount))) return;
    const stakeAmount = parseUnits(`${raceData.usdcAmount}`, usdc.decimals);

    const usdcBalance = await publicClient?.readContract({
      address: usdc.address,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [walletClient.account.address],
    });

    const allowance = await publicClient?.readContract({
      address: usdc.address,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [walletClient.account.address, tokenRacer],
    });

    if (!usdcBalance || !allowance) return;

    if (usdcBalance < stakeAmount) {
      setTransactionStatus(TransactionStatus.INSUFFICIENT_BALANCE);
      return;
    } else if (chainId !== getEnvChain().id) {
      setTransactionStatus(TransactionStatus.WRONG_NETWORK);
      return;
    } else if (allowance < stakeAmount) {
      setTransactionStatus(TransactionStatus.INSUFFICIENT_ALLOWANCE);
      return;
    } else {
      setTransactionStatus(TransactionStatus.READY_TO_SUBMIT);
    }
  }, [walletClient, raceData.usdcAmount, usdc.decimals, usdc.address, publicClient, tokenRacer, chainId]);

  const checkIsActionable = () => {
    switch (transactionStatus) {
      case TransactionStatus.WRONG_NETWORK:
        return true;
      case TransactionStatus.INSUFFICIENT_ALLOWANCE:
        return true;
      case TransactionStatus.INSUFFICIENT_BALANCE:
        return false;
      case TransactionStatus.READY_TO_SUBMIT:
        return true;
      default:
        return false;
    }
  };

  const getTransactionAction = () => {
    if (!isConnected) {
      setShowReview(false);
      return openConnectModal?.();
    }
    if (transactionStatus === TransactionStatus.WRONG_NETWORK) {
      return switchNetwork();
    }
    if (transactionStatus === TransactionStatus.INSUFFICIENT_ALLOWANCE) {
      return approveAllowance();
    }
    if (transactionStatus === TransactionStatus.READY_TO_SUBMIT) {
      return createRace();
    }
  };

  const transactionActionText = useMemo(() => {
    if (!walletClient) return 'Connect Wallet';
    if (isRestricted) return 'Location Restricted';
    if (waitingForTransaction) return 'Submitting...';
    return TransactionStatusText[transactionStatus];
  }, [walletClient, isRestricted, waitingForTransaction, transactionStatus]);

  const switchNetwork = async () => {
    try {
      await walletClient?.switchChain({
        id: getEnvChain().id,
      });
      checkTransactionStatus();
    } catch (error) {
      console.error('Failed to switch network:', error);
    }
  };

  const approveAllowance = async () => {
    try {
      setWaitingForTransaction(true);
      const stakeAmount = parseUnits(`${raceData.usdcAmount}`, usdc.decimals);
      const approveTxHash = await walletClient?.writeContract({
        address: usdc.address,
        abi: erc20Abi,
        functionName: 'approve',
        args: [tokenRacer, stakeAmount],
      });

      if (!approveTxHash) return;

      await publicClient?.waitForTransactionReceipt({
        hash: approveTxHash,
      });

      setWaitingForTransaction(false);
      checkTransactionStatus();
    } catch (error) {
      console.error('Failed to approve allowance:', error);
    } finally {
      setWaitingForTransaction(false);
    }
  };

  const createRace = async () => {
    try {
      setWaitingForTransaction(true);
      const { raceName, raceDuration, startDateTime, handicapSettings, usdcAmount, allocations, creatorCommission } =
        raceData;

      if (
        !raceName ||
        !raceDuration ||
        !startDateTime ||
        !handicapSettings ||
        !usdcAmount ||
        !allocations ||
        !creatorCommission
      )
        return;

      const createRaceTxHash = await walletClient?.writeContract({
        address: tokenRacer,
        abi: tokenRacerAbi,
        functionName: 'createRace',
        args: [
          {
            name: raceName,
            bettingDuration: BigInt(Math.floor(startDateTime.unix() - dayjs().unix())),
            raceDuration: BigInt(raceDuration * 3600),
            creatorFeePercent: parseUnits((creatorCommission / 100).toFixed(2), 18),
            onBehalfOf: walletClient.account.address,
            handicapEnabled: handicapSettings ? handicapSettings.enabled : false,
            tokenIds: selectedTokens.map(token => token.id.toString()),
            initialBets: selectedTokens.map(token =>
              parseUnits(`${(allocations[token.symbol] / 100) * usdcAmount}`, usdc.decimals)
            ),
          },
        ],
      });

      if (!createRaceTxHash) return;

      await publicClient?.waitForTransactionReceipt({
        hash: createRaceTxHash,
      });
      setWaitingForTransaction(false);
      setShowReview(false);
      fetchActiveRaces(true);
      navigate(`/join-races`);
    } catch (error) {
      console.error('Failed to create race:', error);
    } finally {
      setWaitingForTransaction(false);
    }
  };

  useEffect(() => {
    checkTransactionStatus();
  }, [chainId, checkTransactionStatus]);

  useEffect(() => {
    if (showReview) {
      checkTransactionStatus();
    }
  }, [showReview, checkTransactionStatus]);

  return (
    <div className="mx-auto w-full max-w-screen-xl space-y-10 px-6 py-10">
      <div
        className="pointer-events-none relative isolate flex items-center overflow-hidden rounded-3xl border border-[#292828] bg-cover bg-right bg-no-repeat p-6 before:absolute before:-left-9 before:-top-24 before:-z-[1] before:size-[100px] before:rounded-full before:blur-xl before:[background:radial-gradient(#a75eec,transparent)] after:absolute after:inset-0 after:-z-[1] after:bg-black/70 after:bg-cover after:bg-right after:bg-no-repeat md:bg-contain md:p-9 md:before:size-[150px] md:after:bg-black/25 md:after:bg-contain xl:before:size-[250px]"
        style={{ backgroundImage: `url(${wizheader})` }}
      >
        <div className="space-y-3">
          <div className="text-[30px] font-bold">Race Wizard</div>
          <div className="text-sm text-[#9C9C9C]">
            <p>Select Tokens</p>
            <p>Define Race Start & Duration</p>
            <p>Define Victory Metric & Edge</p>
            <p>Invite your Friends to Participate & Watch the Race</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="self-center md:w-[30px] md:self-start">
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
        </div>

        <div className="md:max-w-80 lg:max-w-[400px] lg:shrink-0">
          <StepCards currentStep={currentStep} />
        </div>

        <div className="flex-grow">
          <StepViews
            currentStep={currentStep}
            totalSteps={totalSteps}
            isLastStep={currentStep === totalSteps}
            tokens={selectedTokens}
            initialRaceSettings={raceData}
            onBack={handlePreviousStep}
            onNext={handleNextStep}
          />
        </div>
      </div>

      <RaceReviewModal
        open={showReview}
        onOpenChange={setShowReview}
        raceData={raceData}
        selectedTokens={selectedTokens}
        buttonText={transactionActionText}
        isSubmitDisabled={!checkIsActionable()}
        getTransactionAction={getTransactionAction}
      />
    </div>
  );
};

export default Index;
